import { Component, EventEmitter, Input, OnDestroy, Output, computed, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { CAPTURADORES } from '../pages/dashboard'
import { TIPOS_SEMILLA } from '../../infrastructure/offline/preferencias'
import { numeroDesdeTexto } from '../../infrastructure/util/numero'
import { VisorImagenComponent } from './visor-imagen'
import {
  crearProveedorDashboard,
  crearProductoDashboard,
  type TelaDashboard,
  type FotoProductoDashboard,
} from '../../infrastructure/supabase/crear-desde-dashboard'
import { ErrorImagenNoSoportada } from '../../infrastructure/util/normalizar-imagen'
import type { Proveedor } from '../../domain/entidades'

interface TelaEnFormulario extends TelaDashboard {
  claveLocal: string
  frentePreviewUrl?: string
  reversoPreviewUrl?: string
}

interface FotoEnFormulario extends FotoProductoDashboard {
  claveLocal: string
  previewUrl: string
}

/**
 * Crear proveedor+producto, o solo producto en un proveedor existente, desde
 * el dashboard (pedido de Angie, 2026-09-16, Fase 2). Escribe directo a
 * Supabase (el dashboard siempre tiene internet real) — ver
 * crear-desde-dashboard.ts para la lógica de guardado.
 */
@Component({
  selector: 'app-crear-registro-modal',
  standalone: true,
  imports: [FormsModule, VisorImagenComponent],
  templateUrl: './crear-registro-modal.html',
  styleUrl: './crear-registro-modal.scss',
})
export class CrearRegistroModalComponent implements OnDestroy {
  @Input({ required: true }) proveedores: Proveedor[] = []
  @Output() cerrar = new EventEmitter<void>()
  @Output() creado = new EventEmitter<void>()

  readonly letras = CAPTURADORES
  readonly tiposSugeridos = TIPOS_SEMILLA

  readonly modo = signal<'nuevo-proveedor' | 'producto-existente'>('nuevo-proveedor')

  // Proveedor nuevo
  readonly letra = signal(this.letras[0])
  readonly nombreProveedor = signal('')
  readonly contacto = signal('')
  readonly telefono = signal('')
  readonly tarjetaArchivo = signal<File | undefined>(undefined)
  readonly tarjetaPreviewUrl = signal<string | undefined>(undefined)

  // Proveedor existente — con buscador, para no tener que desplazarse entre
  // decenas de proveedores (pedido explícito: "no toca bajar y encontrar
  // entre tantos el correcto").
  readonly proveedorExistenteCodigo = signal('')
  readonly busquedaProveedor = signal('')
  readonly proveedoresFiltrados = computed(() => {
    const q = this.busquedaProveedor().trim().toLowerCase()
    if (!q) return this.proveedores
    return this.proveedores.filter(
      (p) => p.codigo.toLowerCase().includes(q) || (p.nombre ?? '').toLowerCase().includes(q),
    )
  })

  // Producto — mismos campos que captura mobile al crear (ver comentario en
  // crear-desde-dashboard.ts: categoría/línea/sublínea/precios se completan
  // después en el panel del dashboard, no acá). "Mueble" queda OPCIONAL a
  // propósito — no todo producto capturado en mobile lo trae lleno, y no
  // debe ser un requisito nuevo que mobile no exige.
  readonly mueble = signal('')
  readonly atributosTexto = signal('')
  readonly fob = signal('')
  readonly moq = signal('')
  readonly unidadesPorContenedor = signal('')
  readonly colorEstructuraTexto = signal('')
  readonly colorEstructuraArchivo = signal<File | undefined>(undefined)
  readonly colorEstructuraPreviewUrl = signal<string | undefined>(undefined)
  readonly notas = signal('')

  readonly fotos = signal<FotoEnFormulario[]>([])
  readonly telas = signal<TelaEnFormulario[]>([])

  readonly guardando = signal(false)
  readonly error = signal<string | null>(null)

  /** Vista ampliada de cualquier miniatura (tarjeta, color, fotos, telas) antes de guardar. */
  readonly imagenAmpliada = signal<{ url: string; titulo: string } | null>(null)

  private clave(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
  }

  ngOnDestroy(): void {
    // Si se cierra el modal sin guardar, liberamos toda vista previa creada — ninguna llegó a subirse.
    if (this.tarjetaPreviewUrl()) URL.revokeObjectURL(this.tarjetaPreviewUrl()!)
    if (this.colorEstructuraPreviewUrl()) URL.revokeObjectURL(this.colorEstructuraPreviewUrl()!)
    for (const f of this.fotos()) URL.revokeObjectURL(f.previewUrl)
    for (const t of this.telas()) {
      if (t.frentePreviewUrl) URL.revokeObjectURL(t.frentePreviewUrl)
      if (t.reversoPreviewUrl) URL.revokeObjectURL(t.reversoPreviewUrl)
    }
  }

  cambiarModo(m: 'nuevo-proveedor' | 'producto-existente'): void {
    this.modo.set(m)
  }

  ampliar(url: string, titulo: string): void {
    this.imagenAmpliada.set({ url, titulo })
  }

  cerrarAmpliada(): void {
    this.imagenAmpliada.set(null)
  }

  alElegirTarjeta(evento: Event): void {
    const archivo = (evento.target as HTMLInputElement).files?.[0]
    if (!archivo) return
    if (this.tarjetaPreviewUrl()) URL.revokeObjectURL(this.tarjetaPreviewUrl()!)
    this.tarjetaArchivo.set(archivo)
    this.tarjetaPreviewUrl.set(URL.createObjectURL(archivo))
  }

  alElegirColorEstructura(evento: Event): void {
    const archivo = (evento.target as HTMLInputElement).files?.[0]
    if (!archivo) return
    if (this.colorEstructuraPreviewUrl()) URL.revokeObjectURL(this.colorEstructuraPreviewUrl()!)
    this.colorEstructuraArchivo.set(archivo)
    this.colorEstructuraPreviewUrl.set(URL.createObjectURL(archivo))
  }

  agregarFoto(evento: Event): void {
    const archivo = (evento.target as HTMLInputElement).files?.[0]
    ;(evento.target as HTMLInputElement).value = ''
    if (!archivo) return
    this.fotos.update((f) => [...f, { archivo, etiqueta: '', claveLocal: this.clave(), previewUrl: URL.createObjectURL(archivo) }])
  }

  quitarFoto(claveLocal: string): void {
    const foto = this.fotos().find((x) => x.claveLocal === claveLocal)
    if (foto) URL.revokeObjectURL(foto.previewUrl)
    this.fotos.update((f) => f.filter((x) => x.claveLocal !== claveLocal))
  }

  actualizarEtiquetaFoto(claveLocal: string, etiqueta: string): void {
    this.fotos.update((f) => f.map((x) => (x.claveLocal === claveLocal ? { ...x, etiqueta } : x)))
  }

  agregarTela(): void {
    this.telas.update((t) => [...t, { muestrario: '', colores: '', tipo: '', claveLocal: this.clave() }])
  }

  quitarTela(claveLocal: string): void {
    const tela = this.telas().find((x) => x.claveLocal === claveLocal)
    if (tela?.frentePreviewUrl) URL.revokeObjectURL(tela.frentePreviewUrl)
    if (tela?.reversoPreviewUrl) URL.revokeObjectURL(tela.reversoPreviewUrl)
    this.telas.update((t) => t.filter((x) => x.claveLocal !== claveLocal))
  }

  actualizarTela(claveLocal: string, cambios: Partial<TelaEnFormulario>): void {
    this.telas.update((t) => t.map((x) => (x.claveLocal === claveLocal ? { ...x, ...cambios } : x)))
  }

  alElegirFrenteTela(claveLocal: string, evento: Event): void {
    const archivo = (evento.target as HTMLInputElement).files?.[0]
    if (!archivo) return
    const actual = this.telas().find((x) => x.claveLocal === claveLocal)
    if (actual?.frentePreviewUrl) URL.revokeObjectURL(actual.frentePreviewUrl)
    this.actualizarTela(claveLocal, { frenteArchivo: archivo, frentePreviewUrl: URL.createObjectURL(archivo) })
  }

  alElegirReversoTela(claveLocal: string, evento: Event): void {
    const archivo = (evento.target as HTMLInputElement).files?.[0]
    if (!archivo) return
    const actual = this.telas().find((x) => x.claveLocal === claveLocal)
    if (actual?.reversoPreviewUrl) URL.revokeObjectURL(actual.reversoPreviewUrl)
    this.actualizarTela(claveLocal, { reversoArchivo: archivo, reversoPreviewUrl: URL.createObjectURL(archivo) })
  }

  get puedeGuardar(): boolean {
    if (this.modo() === 'nuevo-proveedor') return this.nombreProveedor().trim() !== ''
    return this.proveedorExistenteCodigo() !== ''
  }

  async guardar(): Promise<void> {
    if (!this.puedeGuardar) return
    this.error.set(null)
    this.guardando.set(true)
    try {
      const proveedorCodigo =
        this.modo() === 'nuevo-proveedor'
          ? await crearProveedorDashboard({
              letra: this.letra(),
              nombre: this.nombreProveedor().trim(),
              contacto: this.contacto().trim(),
              telefono: this.telefono().trim(),
              tarjetaArchivo: this.tarjetaArchivo(),
            })
          : this.proveedorExistenteCodigo()

      await crearProductoDashboard({
        proveedorCodigo,
        mueble: this.mueble().trim(),
        atributosEspeciales: this.atributosTexto()
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        fob: numeroDesdeTexto(this.fob()),
        moq: this.moq().trim() || undefined,
        unidadesPorContenedor: this.unidadesPorContenedor().trim() || undefined,
        colorEstructura: this.colorEstructuraTexto().trim(),
        colorEstructuraArchivo: this.colorEstructuraArchivo(),
        notas: this.notas().trim(),
        fotos: this.fotos(),
        telas: this.telas(),
      })

      this.creado.emit()
    } catch (err) {
      this.error.set(err instanceof ErrorImagenNoSoportada ? err.message : 'No se pudo guardar. Intenta de nuevo.')
    } finally {
      this.guardando.set(false)
    }
  }
}
